import React, { useEffect, useRef, useState } from 'react';

// ══════════════════════════════════════════════════════════════════════════════
// A.D.M — Location Intelligence Viewer
// Primary: Mapbox satellite imagery (high quality, no Street View limitations)
// Fallback: Google Maps embed
// Shows exact incident location with satellite overlay
// ══════════════════════════════════════════════════════════════════════════════

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';
const GOOGLE_KEY   = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

export default function StreetView({ lat, lng, name, region, onClose, onBack }) {
  const mapRef    = useRef(null);
  const mbMapRef  = useRef(null);
  const [mode,    setMode]    = useState('satellite'); // satellite | street | hybrid
  const [loaded,  setLoaded]  = useState(false);
  const [error,   setError]   = useState(null);
  const [zoom,    setZoom]    = useState(14);

  const C = {
    bg:     '#07090c', border:'rgba(29,60,40,0.4)', green:'#1de050',
    dim:    'rgba(155,195,168,0.65)', text:'#e2ede6',
  };

  // ── Load Mapbox GL JS ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!lat || !lng) { setError('No coordinates for this location'); return; }
    if (!MAPBOX_TOKEN) { setError('MAPBOX_TOKEN not configured'); return; }

    // Load Mapbox GL CSS
    if (!document.getElementById('mapbox-css')) {
      const link  = document.createElement('link');
      link.id     = 'mapbox-css';
      link.rel    = 'stylesheet';
      link.href   = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
      document.head.appendChild(link);
    }

    // Load Mapbox GL JS
    const loadMapbox = () => new Promise((resolve, reject) => {
      if (window.mapboxgl) { resolve(); return; }
      if (document.getElementById('mapbox-js')) {
        const wait = setInterval(() => { if (window.mapboxgl) { clearInterval(wait); resolve(); } }, 100);
        return;
      }
      const script   = document.createElement('script');
      script.id      = 'mapbox-js';
      script.src     = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js';
      script.onload  = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    loadMapbox().then(() => {
      if (!mapRef.current || mbMapRef.current) return;
      const mapbox = window.mapboxgl;
      mapbox.accessToken = MAPBOX_TOKEN;

      const map = new mapbox.Map({
        container:    mapRef.current,
        style:        'mapbox://styles/mapbox/satellite-streets-v12',
        center:       [lng, lat],
        zoom:         14,
        pitch:        45,       // Slight 3D tilt
        bearing:      -15,
        antialias:    true,
        attributionControl: false,
      });

      mbMapRef.current = map;

      map.on('load', () => {
        setLoaded(true);

        // Add terrain for 3D effect
        map.addSource('mapbox-dem', {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 512, maxzoom: 14,
        });
        map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
        map.setFog({ color: 'rgb(4,8,6)', 'horizon-blend': 0.1 });

        // Add incident marker
        const el       = document.createElement('div');
        el.style.cssText = `
          width:36px; height:36px;
          border:3px solid #1de050;
          border-radius:50%;
          background:rgba(29,224,80,0.25);
          box-shadow:0 0 20px rgba(29,224,80,0.6);
          animation: marker-pulse 1.5s ease-in-out infinite;
          cursor:pointer;
        `;

        // Pulse animation
        const style = document.createElement('style');
        style.textContent = `@keyframes marker-pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.3);opacity:0.7} }`;
        document.head.appendChild(style);

        new mapbox.Marker({ element: el })
          .setLngLat([lng, lat])
          .setPopup(new mapbox.Popup({ offset:25, className:'adm-popup' })
            .setHTML(`<div style="background:#07090c;color:#1de050;padding:10px;font-family:monospace;font-size:11px;border:1px solid rgba(29,224,80,0.3);border-radius:6px">
              <div style="font-weight:700;margin-bottom:4px">${name||'UAP Incident'}</div>
              <div style="color:#8ab899;font-size:10px">${region||''}</div>
              <div style="color:#556b5e;font-size:9px;margin-top:4px">${lat.toFixed(6)}, ${lng.toFixed(6)}</div>
            </div>`))
          .addTo(map);
      });

      map.on('zoom', () => setZoom(Math.round(map.getZoom())));
      map.on('error', () => setError('Map load error'));

    }).catch(() => setError('Failed to load Mapbox'));

    return () => {
      if (mbMapRef.current) {
        mbMapRef.current.remove();
        mbMapRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  // Switch map style
  const switchStyle = (style) => {
    if (!mbMapRef.current) return;
    const styles = {
      satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
      hybrid:    'mapbox://styles/mapbox/satellite-v9',
      terrain:   'mapbox://styles/mapbox/outdoors-v12',
    };
    mbMapRef.current.setStyle(styles[style] || styles.satellite);
    setMode(style);
  };

  // Zoom controls
  const zoomIn  = () => mbMapRef.current?.zoomIn();
  const zoomOut = () => mbMapRef.current?.zoomOut();
  const resetView = () => {
    mbMapRef.current?.flyTo({ center:[lng,lat], zoom:14, pitch:45, bearing:-15, duration:1500 });
  };

  const btnStyle = (active) => ({
    padding: '6px 12px', fontSize: 10, fontWeight: 700, cursor: 'pointer',
    fontFamily: 'monospace', letterSpacing: 0.5,
    borderRadius: 5, WebkitTapHighlightColor: 'transparent',
    background: active ? 'rgba(29,224,80,0.2)' : 'rgba(8,14,10,0.8)',
    color:      active ? C.green : C.dim,
    border: `1px solid ${active ? 'rgba(29,224,80,0.4)' : 'rgba(29,60,40,0.3)'}`,
  });

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:40,
      background:C.bg, display:'flex', flexDirection:'column',
    }}>
      {/* Header */}
      <div style={{
        display:'flex', alignItems:'center', gap:12,
        padding:'max(14px,calc(env(safe-area-inset-top)+10px)) 16px 12px',
        borderBottom:`1px solid ${C.border}`, flexShrink:0,
        background:'rgba(4,8,6,0.95)', backdropFilter:'blur(12px)',
      }}>
        <button onClick={onBack||onClose} style={{background:'none',border:'none',color:C.dim,fontSize:20,cursor:'pointer',lineHeight:1,padding:4,WebkitTapHighlightColor:'transparent'}}>
          ‹
        </button>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{name||'Location Intelligence'}</div>
          <div style={{fontSize:10,color:C.dim,marginTop:1}}>{region||''} · {lat?.toFixed(4)}, {lng?.toFixed(4)}</div>
        </div>
        <button onClick={onClose} style={{background:'none',border:'none',color:C.dim,fontSize:18,cursor:'pointer',lineHeight:1,WebkitTapHighlightColor:'transparent'}}>✕</button>
      </div>

      {/* Map controls */}
      <div style={{
        display:'flex', gap:6, padding:'8px 12px',
        background:'rgba(4,8,6,0.9)', borderBottom:`1px solid ${C.border}`,
        flexShrink:0, flexWrap:'wrap', alignItems:'center',
      }}>
        <button style={btnStyle(mode==='satellite')} onClick={()=>switchStyle('satellite')}>SATELLITE</button>
        <button style={btnStyle(mode==='hybrid')}    onClick={()=>switchStyle('hybrid')}>AERIAL</button>
        <button style={btnStyle(mode==='terrain')}   onClick={()=>switchStyle('terrain')}>TERRAIN</button>
        <div style={{flex:1}}/>
        <div style={{display:'flex',gap:4}}>
          <button onClick={zoomIn}    style={{...btnStyle(false),padding:'5px 10px',fontSize:14}}>+</button>
          <button onClick={zoomOut}   style={{...btnStyle(false),padding:'5px 10px',fontSize:14}}>−</button>
          <button onClick={resetView} style={{...btnStyle(false),padding:'5px 10px',fontSize:11}}>⌖</button>
        </div>
        <div style={{fontSize:9,color:'rgba(29,224,80,0.4)',fontFamily:'monospace',marginLeft:4}}>Z{zoom}</div>
      </div>

      {/* Map container */}
      <div style={{flex:1,position:'relative',overflow:'hidden'}}>
        {error ? (
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',gap:12,padding:24}}>
            <div style={{fontSize:32}}>🛰</div>
            <div style={{fontSize:14,color:C.dim,textAlign:'center'}}>{error}</div>
            {!MAPBOX_TOKEN && (
              <div style={{fontSize:11,color:'rgba(100,140,120,0.5)',textAlign:'center',maxWidth:300}}>
                Add REACT_APP_MAPBOX_TOKEN to Netlify environment variables.<br/>
                Get a free token at mapbox.com
              </div>
            )}
            {/* Google Maps fallback */}
            {GOOGLE_KEY && lat && lng && (
              <div style={{width:'100%',height:300,marginTop:12}}>
                <iframe
                  title="location"
                  width="100%" height="100%"
                  style={{border:'none',borderRadius:8}}
                  src={`https://www.google.com/maps/embed/v1/view?key=${GOOGLE_KEY}&center=${lat},${lng}&zoom=14&maptype=satellite`}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            <div ref={mapRef} style={{width:'100%',height:'100%'}}/>
            {!loaded && (
              <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(4,8,6,0.8)',flexDirection:'column',gap:12}}>
                <div style={{width:36,height:36,border:'3px solid rgba(29,224,80,0.2)',borderTop:'3px solid #1de050',borderRadius:'50%',animation:'adm-spin 0.8s linear infinite'}}/>
                <div style={{fontSize:11,color:C.dim,fontFamily:'monospace'}}>LOADING SATELLITE IMAGERY...</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom info bar */}
      <div style={{
        padding:'10px 16px', borderTop:`1px solid ${C.border}`,
        background:'rgba(4,8,6,0.95)', flexShrink:0,
        paddingBottom:'max(10px,calc(env(safe-area-inset-bottom)+10px))',
        display:'flex', alignItems:'center', justifyContent:'space-between',
      }}>
        <div style={{fontSize:9,fontFamily:'monospace',color:'rgba(29,224,80,0.35)',letterSpacing:1}}>
          MAPBOX SATELLITE · A.D.M LOCATION INTEL
        </div>
        <a
          href={`https://www.google.com/maps/@${lat},${lng},500m/data=!3m1!1e3`}
          target="_blank" rel="noopener noreferrer"
          style={{fontSize:9,color:'rgba(29,224,80,0.4)',textDecoration:'none',fontFamily:'monospace',letterSpacing:1}}
        >
          OPEN IN GOOGLE EARTH ↗
        </a>
      </div>
    </div>
  );
}
